/**
 * Platform settings — deployment-wide configuration (redesign phase 5, §2.7).
 *
 * ── The container, and why it is not empty ──────────────────────────────
 *
 * Everything platform-wide used to be either buried inside the catalog screen
 * (the billing settings, including the credit rate — i.e. the gross margin) or
 * an environment variable requiring a redeploy (the grace ceiling). This is the
 * surface those belong on, seeded with what already existed rather than shipped
 * as a promise.
 *
 * ── Two pairings that are the whole point ───────────────────────────────
 *
 * A number the operator is expected to SET is shown beside its own OUTCOME:
 *
 *  - the credit rate beside **realised margin** (C3) — what customers actually
 *    paid per dollar of provider cost, so the markup is set against evidence
 *    rather than blind;
 *  - the grace ceiling beside **grace absorbed this period** (C7) — money
 *    already given away, against the ceiling that bounds it.
 *
 * Both figures come from `GET /api/admin/overview`, the same endpoint the
 * Overview screen renders, so the two screens cannot disagree about them.
 *
 * ── What is deliberately read-only (C6) ─────────────────────────────────
 *
 * `PLATFORM_ADMIN_EMAILS` stays in the environment: it is the brake that stops
 * the platform-admin set self-propagating, since `guardPlatformAdminGrant`
 * requires any new owner/admin of the platform org to be on that list. A field
 * editing it here would let a platform admin grant platform admin to anyone.
 * Only the COUNT is shown, never the addresses. `BILLING_ENABLED` gates route
 * registration at boot and so cannot be a row without the flag and the mounted
 * routers disagreeing until restart.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm, useBillingCurrency, useDraftState } from '@papan/core-ui/ui'
import { formatMoney, formatCurrencyMinor, parseDiskAlarmEmails, invalidDiskAlarmEmails, type ChatCostVisibility, MAX_DISK_ALARM_RECIPIENTS } from '@papan/types'
import { BillingSettingsSection } from '@papan/billing/ui'
import {
  budgetApi,
  type PlatformSettingsResponse,
  type OverviewResponse,
} from '../../client/budget'
import { Section, StatTile, ReadOnlyNotice } from './bits'
import { PersonalTenantModeSetting } from './PersonalTenantModeSetting'
import { VerifiedAppsSetting } from './VerifiedAppsSetting'

/** Microdollars → a plain dollar string for the input, and back. The stored
 *  unit is microdollars; nobody types those. */
/**
 * Billing-currency MICRO ↔ an editable major-unit string.
 *
 * NO CURRENCY ARGUMENT, and that is the point: a micro unit is 10^-6 of the
 * MAJOR unit in every currency, unlike a MINOR unit (100 per major in MYR, 1 in
 * JPY, 1000 in BHD). Reaching for the exponent here would be the error.
 *
 * It was named `toDollars` / `parseDollars` while the grace ceiling was USD.
 */
const toMajor = (micro: number) => (micro / 1_000_000).toString()
function parseMajor(text: string): number | null {
  const t = text.trim()
  if (!t) return null
  const n = Number(t)
  // Rejects NaN, Infinity and negatives. `0` passes deliberately — it is the
  // "grace off" setting, not an empty field.
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 1_000_000)
}

export function PlatformSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation('core-ui')
  const [data, setData] = useState<PlatformSettingsResponse | null>(null)
  const [overview, setOverview] = useState<OverviewResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await budgetApi.getPlatformSettings())
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load platform settings')
    } finally {
      setLoading(false)
    }
    // The outcome figures are a SEPARATE, best-effort read: margin and grace
    // consumption are context for the settings, not a precondition for editing
    // them. A failure here must not block the form.
    try {
      setOverview(await budgetApi.getOverview())
    } catch {
      setOverview(null)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (!data) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  return (
    <div className="pb-12">
      <h2 className="themed-text text-lg font-medium mb-1">
        {t('settings.platform.nav.settings', { defaultValue: 'Platform settings' })}
      </h2>
      <p className="themed-text-muted text-sm mb-4">
        {t('settings.platform.settings.desc', {
          defaultValue:
            'Configuration for this whole deployment, and the numbers that decide the margin. Each setting is shown beside the outcome it produces.',
        })}
      </p>

      {!canEdit && <ReadOnlyNotice />}

      {/* FIRST, and it used to be last.
          ────────────────────────────────────────────────────────────────
          The old order put it "beneath the two money knobs because those are
          numbers, and this is a mode". That reasoning was sound when the block
          held one radio pair. It no longer does: the same block now carries the
          DEFAULT PRESET every console-created customer is seeded from and the
          fallback plan a lapsed one is moved to — and the mode gates whether
          either does anything, since `plansAreMeaningful` turns entitlements,
          the model plan layer and the upload freeze off underneath it.

          So an operator setting this deployment up met the question "which
          preset seeds new customers?" *after* scrolling past a grace ceiling and
          a free-org limit, and met the switch that decides whether their answer
          matters at all below both. A page reads top-down; the thing everything
          else is conditional on goes at the top. */}
      <PlanAuthoritySetting data={data} canEdit={canEdit} onSaved={load} />

      {/* Charging config, moved off the price book. Its own component with its
          own fetch and save — see BillingSettingsSection. */}
      <BillingSettingsSection canEdit={canEdit} />

      {overview?.billingEnabled && <MarginEvidence margin={overview.margin} />}

      <GraceSetting
        data={data}
        absorbedBillingMicro={overview?.grace.absorbedBillingMicro ?? null}
        graceTenants={overview?.grace.tenants ?? null}
        canEdit={canEdit}
        onSaved={load}
      />

      <FreeOrgsSetting data={data} canEdit={canEdit} onSaved={load} />

      {/* Beside the free-org limit, not beside the money knobs.
          ────────────────────────────────────────────────────────────────
          Both are TENANCY rules — "how many organizations may someone make"
          and "what is a personal tenant" are the same question asked twice,
          and an operator configuring a Gnexis-style deployment answers them
          together. The grace ceiling and the credit rate above are money.

          Below rather than above `FreeOrgsSetting` because it is the larger
          statement of the two: the free-org limit bounds a rate, this decides
          what the product IS for every person on the deployment. Reading
          top-down, the narrower rule earns its place first. */}
      <PersonalTenantModeSetting data={data} canEdit={canEdit} onSaved={load} />

      {/* Beside the tenancy rules, for the same reason the mode is: "does the
          keybook exist here" is a statement about what the product IS on this
          deployment, not about money. */}
      <KeybookSetting data={data} canEdit={canEdit} onSaved={load} />

      {/* Beside Keybook, for the same reason: a drive-behavior default rather
          than money, and — like the tenancy rules above — a tenant may
          override it on its own Settings → Storage page (#452). */}
      <VersionRetentionSetting data={data} canEdit={canEdit} onSaved={load} />

      <ChatCostSetting data={data} canEdit={canEdit} onSaved={load} />

      {/* Which AI apps the deployment vouches for (0322) — what every person's consent page and Connected apps show
          as verified, with the logos uploaded here. */}
      <VerifiedAppsSetting canEdit={canEdit} />

      {/* Operational, not money — but it lives on this screen because it is a
          deployment behaviour a platform admin sets, and (0200) putting it here
          is what took the disk-alarm's second channel out of per-host env and
          off SSH. */}
      <DiskAlarmWebhookSetting data={data} canEdit={canEdit} onSaved={load} />
      <DiskAlarmEmailsSetting data={data} canEdit={canEdit} onSaved={load} />
    </div>
  )
}

/**
 * Realised margin, rendered where the credit rate is EDITED (C3) — and, since
 * F-34, the ONE place the full breakdown lives.
 *
 * Overview and this screen used to render the same four figures under two
 * headings, with neither naming the other. Overview now keeps the headline
 * comparison (realised against set) and links here, because this is the moment
 * the numbers change a decision: someone has their hand on the input.
 *
 * So the caveat and the excluded-invoice note moved here with the figures. A
 * breakdown that omits "these two invoices are in another currency and are not
 * in the total" is a total that does not add up, and the reader most likely to
 * act on it is the one on this screen.
 */
function MarginEvidence({ margin }: { margin: OverviewResponse['margin'] }) {
  const { t } = useTranslation('core-ui')
  const realised = margin.realisedRateMinorPerUsd
  // Below the configured rate is not automatically bad — see the caveat — so
  // this only selects which caveat to show, and is never an alarm.
  const below = realised != null && realised < margin.creditRateMinorPerUsd
  return (
    <Section
      title={t('settings.platform.settings.marginTitle', { defaultValue: 'Is that markup working?' })}
      desc={t('settings.platform.settings.marginDesc', {
        defaultValue:
          'What this period actually collected, per dollar of provider cost, against the credit rate set above.',
      })}
    >
      <div className="flex flex-wrap gap-2">
        <StatTile
          label={t('settings.platform.overview.realisedRate', { defaultValue: 'Realised rate' })}
          value={realised == null
            ? '—'
            : t('settings.platform.overview.perUsd', {
                defaultValue: '{{amount}} / US$1',
                amount: formatCurrencyMinor(Math.round(realised), margin.currency),
              })}
        />
        <StatTile
          label={t('settings.platform.overview.configuredRate', { defaultValue: 'Credit rate (set)' })}
          value={t('settings.platform.overview.perUsd', {
            defaultValue: '{{amount}} / US$1',
            amount: formatCurrencyMinor(margin.creditRateMinorPerUsd, margin.currency),
          })}
        />
        <StatTile
          label={t('settings.platform.overview.revenue', { defaultValue: 'Collected' })}
          value={formatCurrencyMinor(margin.revenueMinor, margin.currency)}
          hint={t('settings.platform.overview.revenueHint', {
            defaultValue: 'Credit purchases plus paid subscription invoices, in the currency you charge in.',
          })}
        />
        <StatTile
          label={t('settings.platform.overview.providerCost', { defaultValue: 'Provider cost' })}
          value={formatMoney(margin.providerCostUsd)}
        />
      </div>
      <p className="themed-text-muted text-xs mt-2">
        {below
          ? t('settings.platform.overview.marginTiming', {
              defaultValue:
                'Prepaid credit is bought in one period and spent in another, so a single period\'s realised rate tracks when tenants happened to buy — not the margin. Compare it across several periods before changing the credit rate.',
            })
          : t('settings.platform.overview.marginTimingNeutral', {
              defaultValue:
                'Credit bought in one period is often spent in another, so read this across several periods rather than one.',
            })}
      </p>
      {margin.excludedInvoices > 0 && (
        <p className="themed-text-muted text-xs mt-1">
          {t('settings.platform.overview.excludedInvoices', {
            defaultValue:
              '{{n}} paid invoice(s) in another currency are not included — they cannot be added to a total labelled {{cur}}.',
            n: margin.excludedInvoices,
            cur: margin.currency.toUpperCase(),
          })}
        </p>
      )}
    </Section>
  )
}

/**
 * The grace ceiling (C7), with what it has already cost this period.
 *
 * ── Three states, and none of them is "empty" ───────────────────────────
 *
 * `null` (blank input) = inherit `PAPAN_PLATFORM_GRACE_CAP`, else $5.
 * `0` = grace OFF globally. A positive number = that ceiling, per tenant, per
 * period. A control that coerced blank→0 would silently switch off a benefit
 * every customer currently has; one that coerced 0→blank would silently turn
 * it back on. So the input carries an explicit "inherit" affordance rather
 * than relying on emptiness to mean something.
 */
function GraceSetting({
  data, absorbedBillingMicro, graceTenants, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  absorbedBillingMicro: number | null
  graceTenants: number | null
  canEdit: boolean
  onSaved: () => void
}) {
  /**
   * What the deployment CHARGES in — the unit the ceiling, the input's label
   * and the absorbed total are all in.
   *
   * FROM THE PROVIDER, not from this page's `/overview` payload. It arrived as
   * `overview?.margin.currency ?? 'usd'`, and that fallback is live in two
   * states, both wrong on any non-USD deployment: while the request is in
   * flight, and FOREVER if it fails. Measured on a MYR install, the section
   * rendered "Per tenant, per period (USD)" over "$5.00" for ~250ms before
   * correcting to MYR — a money figure whose only defect is its symbol, which
   * is the failure mode `BillingCurrencyProvider` was built for.
   *
   * It also made one fact have two sources: `BillingSettingsSection`, higher up
   * this same page, reads the currency from `billing_settings` — the authority.
   * The provider fetches that once for the whole app, so it is both earlier and
   * not contingent on a console-only endpoint.
   */
  const billingCurrency = useBillingCurrency()
  const graceMoney = { currency: billingCurrency, rate: 1 }
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const stored = data.settings.graceCapBillingMicro
  // A draft over the stored value (#897, kept across a phone/desktop layout
  // swap): until typed in, the field shows what PERSISTED (a reload shows
  // through); once typed in, no reload overwrites it. Save / Discard drop it.
  const [text, setText, { clear: clearText, settle: settleText }] = useDraftState<string>('platform:grace', () => (stored == null ? '' : toMajor(stored)))
  const [saving, setSaving] = useState(false)

  const inherit = text.trim() === ''
  const parsed = inherit ? null : parseMajor(text)
  const invalid = !inherit && parsed == null
  const dirty = inherit ? stored != null : parsed !== stored

  const save = async () => {
    if (invalid) return
    setSaving(true)
    try {
      await budgetApi.setPlatformSettings({ graceCapBillingMicro: parsed })
      // Against what the reload renders for the sent value ("5.5" shows as the
      // stored "5.5", "05" as "5") — another section's reload may already have
      // brought it, and then the fallback never moves again.
      settleText(parsed == null ? '' : toMajor(parsed))
      toast.success(t('settings.platform.settings.graceSaved', { defaultValue: 'Grace ceiling updated.' }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const resolved = data.resolved.graceCapBillingMicro
  const source = data.resolved.graceCapSource

  return (
    <Section
      title={t('settings.platform.settings.graceTitle', { defaultValue: 'Grace ceiling' })}
      desc={t('settings.platform.settings.graceDesc', {
        defaultValue:
          'How much of its own money the platform will absorb for one tenant in one period, after that tenant\'s budget has run out. Total exposure is this multiplied by every tenant.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3">
        <div className="flex flex-wrap items-start gap-4">
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-[11px]">
              {/* The ceiling is denominated in the money the deployment
                  CHARGES in, not in USD — grace is the last rung of the funding
                  waterfall and every rung above it is billing-native. */}
              {t('settings.platform.settings.gracePerTenant', {
                defaultValue: 'Per tenant, per period ({{currency}})',
                currency: billingCurrency.toUpperCase(),
              })}
            </span>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={saving || !canEdit}
              inputMode="decimal"
              placeholder={t('settings.platform.settings.graceInherit', { defaultValue: 'Inherit' })}
              aria-label={t('settings.platform.settings.gracePerTenant', {
                defaultValue: 'Per tenant, per period ({{currency}})',
                currency: billingCurrency.toUpperCase(),
              })}
              className={`themed-input border rounded px-2 py-1 text-xs w-28 outline-none focus:border-blue-500 disabled:opacity-50 ${
                invalid ? 'border-red-500' : ''
              }`}
            />
            <span className={`text-[10px] ${invalid ? 'text-red-400' : 'themed-text-muted'}`}>
              {invalid
                ? t('settings.platform.settings.graceInvalid', {
                    defaultValue: 'Enter a non-negative amount, or leave blank to inherit.',
                  })
                : inherit
                  ? t('settings.platform.settings.graceBlankMeans', {
                      defaultValue: 'Blank = inherit the deployment default.',
                    })
                  : parsed === 0
                    ? t('settings.platform.settings.graceZeroMeans', {
                        defaultValue: 'Zero switches grace off for every tenant.',
                      })
                    : t('settings.platform.settings.graceSetMeans', {
                        defaultValue: 'Absorbed per tenant before AI is refused outright.',
                      })}
            </span>
          </label>

          <StatTile
            label={t('settings.platform.settings.graceInForce', { defaultValue: 'In force' })}
            value={resolved === 0
              ? t('settings.platform.overview.graceOff', { defaultValue: 'Off' })
              : formatMoney(resolved, graceMoney)}
            hint={
              // Naming the SOURCE is what makes a blank field legible: the
              // operator can see a live ceiling and know why the box is empty.
              source === 'setting'
                ? t('settings.platform.settings.srcSetting', { defaultValue: 'Set here.' })
                : source === 'env'
                  ? t('settings.platform.settings.srcEnv', {
                      defaultValue: 'Inherited from PAPAN_PLATFORM_GRACE_CAP.',
                    })
                  : t('settings.platform.settings.srcDefault', {
                      defaultValue: 'The built-in default — nothing has been configured.',
                    })
            }
          />

          {absorbedBillingMicro != null && (
            <StatTile
              label={t('settings.platform.overview.graceThisPeriod', { defaultValue: 'Given away this period' })}
              value={formatMoney(absorbedBillingMicro, graceMoney)}
              hint={graceTenants != null
                ? t('settings.platform.settings.graceTenantsHint', {
                    defaultValue: '{{n}} tenants used it.',
                    n: graceTenants,
                  })
                : undefined}
            />
          )}
        </div>

        {canEdit && dirty && (
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={save}
              disabled={saving || invalid}
              className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
            >
              {saving ? t('common:saving') : t('common:save')}
            </button>
            <button
              onClick={clearText}
              disabled={saving}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
            >
              {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

/**
 * How many free organizations one person may create for themselves (phase 6).
 *
 * Read-only on this screen until 0156, with a note saying it would become a
 * setting when creating customers from the console did. It did, so it has —
 * and the two are related in a way worth stating on the screen: the limit
 * governs SELF-SERVICE creation only. `+ New organization` here ignores it,
 * because a platform admin provisioning a paying customer is not the case the
 * limit exists for.
 *
 * `0` is a real, useful setting rather than a degenerate one: it turns
 * self-service creation off entirely, for a deployment where every organization
 * is meant to be provisioned by an operator. Blank inherits the shipped 2. As
 * with the grace ceiling, blank and zero must not collapse into each other.
 */
function FreeOrgsSetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const stored = data.settings.maxFreeOrgsPerUser
  // A draft over the stored value (#897) — see GraceSetting.
  const [text, setText, { clear: clearText, settle: settleText }] = useDraftState<string>('platform:freeOrgs', () => (stored == null ? '' : String(stored)))
  const [saving, setSaving] = useState(false)

  const inherit = text.trim() === ''
  const n = Number(text.trim())
  const parsed = inherit ? null : (Number.isInteger(n) && n >= 0 ? n : null)
  const invalid = !inherit && parsed == null
  const dirty = inherit ? stored != null : parsed !== stored

  const save = async () => {
    if (invalid) return
    setSaving(true)
    try {
      await budgetApi.setPlatformSettings({ maxFreeOrgsPerUser: parsed })
      settleText(parsed == null ? '' : String(parsed)) // as the grace row
      toast.success(t('settings.platform.settings.freeOrgsSaved', { defaultValue: 'Limit updated.' }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section
      title={t('settings.platform.settings.freeOrgsTitle', { defaultValue: 'Self-service organizations' })}
      desc={t('settings.platform.settings.freeOrgsDesc', {
        defaultValue:
          'How many free organizations one person may create for themselves. Provisioning from Tenants ignores this — it exists to stop one account minting unlimited free tenants, which is not what an operator setting up a tenant is doing.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3">
        <div className="flex flex-wrap items-start gap-4">
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-[11px]">
              {t('settings.platform.settings.freeOrgsPerUser', { defaultValue: 'Per user' })}
            </span>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={saving || !canEdit}
              inputMode="numeric"
              placeholder={t('settings.platform.settings.graceInherit', { defaultValue: 'Inherit' })}
              aria-label={t('settings.platform.settings.freeOrgsPerUser', { defaultValue: 'Per user' })}
              className={`themed-input border rounded px-2 py-1 text-xs w-28 outline-none focus:border-blue-500 disabled:opacity-50 ${
                invalid ? 'border-red-500' : ''
              }`}
            />
            <span className={`text-[10px] ${invalid ? 'text-red-400' : 'themed-text-muted'}`}>
              {invalid
                ? t('settings.platform.settings.freeOrgsInvalid', {
                    defaultValue: 'Enter a whole number of 0 or more, or leave blank to inherit.',
                  })
                : inherit
                  ? t('settings.platform.settings.graceBlankMeans', {
                      defaultValue: 'Blank = inherit the deployment default.',
                    })
                  : parsed === 0
                    ? t('settings.platform.settings.freeOrgsZeroMeans', {
                        defaultValue: 'Zero turns self-service creation off — every org is provisioned here.',
                      })
                    : t('settings.platform.settings.freeOrgsSetMeans', {
                        defaultValue: 'Beyond this, a user must upgrade an existing org.',
                      })}
            </span>
          </label>

          <StatTile
            label={t('settings.platform.settings.graceInForce', { defaultValue: 'In force' })}
            value={data.resolved.maxFreeOrgsPerUser === 0
              ? t('settings.platform.settings.freeOrgsOff', { defaultValue: 'Off' })
              : String(data.resolved.maxFreeOrgsPerUser)}
            hint={stored != null
              ? t('settings.platform.settings.srcSetting', { defaultValue: 'Set here.' })
              : t('settings.platform.settings.srcDefault', {
                  defaultValue: 'The built-in default — nothing has been configured.',
                })}
          />
        </div>

        {canEdit && dirty && (
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={save}
              disabled={saving || invalid}
              className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
            >
              {saving ? t('common:saving') : t('common:save')}
            </button>
            <button
              onClick={clearText}
              disabled={saving}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
            >
              {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

/**
 * How many superseded versions of a file are kept, deployment-wide (#452).
 *
 * Same shape as `FreeOrgsSetting`: blank inherits the shipped default (50), and
 * blank/zero must not collapse into each other — except here `0` is not a real
 * setting the way it is for the free-org limit ("zero turns self-service off").
 * A retention count of 0 would mean "keep nothing", which is not a state the
 * resolver's clamp should have to silently correct for the operator — so the
 * bound is 1..500, enforced identically here and on the route.
 *
 * A tenant's own Settings → Storage page can set a CEILING that overrides this
 * default for that tenant alone (`resolveVersionRetention`: tenant ceiling →
 * platform default → shipped default) — this control is only the second link.
 */
function VersionRetentionSetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const stored = data.settings.defaultVersionRetention
  // A draft over the stored value (#897) — see GraceSetting.
  const [text, setText, { clear: clearText, settle: settleText }] = useDraftState<string>('platform:versionRetention', () => (stored == null ? '' : String(stored)))
  const [saving, setSaving] = useState(false)

  const inherit = text.trim() === ''
  const n = Number(text.trim())
  const parsed = inherit ? null : (Number.isInteger(n) && n >= 1 && n <= 500 ? n : null)
  const invalid = !inherit && parsed == null
  const dirty = inherit ? stored != null : parsed !== stored

  const save = async () => {
    if (invalid) return
    setSaving(true)
    try {
      await budgetApi.setPlatformSettings({ defaultVersionRetention: parsed })
      settleText(parsed == null ? '' : String(parsed)) // as the grace row
      toast.success(t('settings.platform.settings.versionRetentionSaved'))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section
      title={t('settings.platform.settings.versionRetentionTitle')}
      desc={t('settings.platform.settings.versionRetentionDesc')}
    >
      <div className="themed-bg-secondary rounded-lg p-3">
        <div className="flex flex-wrap items-start gap-4">
          <label className="flex flex-col gap-1">
            <span className="themed-text-secondary text-[11px]">
              {t('settings.platform.settings.versionRetentionPerFile')}
            </span>
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={saving || !canEdit}
              inputMode="numeric"
              placeholder={t('settings.platform.settings.graceInherit')}
              aria-label={t('settings.platform.settings.versionRetentionPerFile')}
              className={`themed-input border rounded px-2 py-1 text-xs w-28 outline-none focus:border-blue-500 disabled:opacity-50 ${
                invalid ? 'border-red-500' : ''
              }`}
            />
            <span className={`text-[10px] ${invalid ? 'text-red-400' : 'themed-text-muted'}`}>
              {invalid
                ? t('settings.platform.settings.versionRetentionInvalid')
                : inherit
                  ? t('settings.platform.settings.graceBlankMeans')
                  : t('settings.platform.settings.versionRetentionSetMeans')}
            </span>
          </label>

          <StatTile
            label={t('settings.platform.settings.graceInForce')}
            value={String(data.resolved.defaultVersionRetention)}
            hint={stored != null
              ? t('settings.platform.settings.srcSetting')
              : t('settings.platform.settings.versionRetentionShipped')}
          />
        </div>

        {canEdit && dirty && (
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={save}
              disabled={saving || invalid}
              className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
            >
              {saving ? t('common:saving') : t('common:save')}
            </button>
            <button
              onClick={clearText}
              disabled={saving}
              className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
            >
              {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

/**
 * Plan authority, and the fallback plan that only means anything under it
 * (0158/0170, manual billing decisions 1 and 2).
 *
 * ── Why the two controls are one card ───────────────────────────────────
 *
 * The fallback plan is meaningless while plans come from the provider — Stripe's
 * own cancellation flow reverts a customer to free, and nothing reads this
 * column. Putting it in its own Section would leave a live-looking picker on
 * every provider-billed deployment with no way to say why it does nothing. Here
 * it is visibly *inside* the mode, and disabled outside it.
 *
 * ── Why the switch writes immediately and the picker stages ──────────────
 *
 * Consistent with the rest of the console: a binary choice has no value to
 * mistype, so the two mode buttons commit (the same reasoning the enforcement-mode
 * buttons on the org budget page use). The plan picker is a choice among many and
 * stages behind Save.
 *
 * ── `plansAreMeaningful` is DISPLAYED, never re-derived ─────────────────
 *
 * It is neither "authority is manual" nor "billing is on" but the combination, and
 * the server resolves it. The one cell worth naming on screen is
 * `provider` + billing-off: plans mean nothing there, which is the behaviour that
 * shipped before any of this existed and must stay that way — `applyEntitlements`
 * runs from `onSeatCountChanged`, so a version that applied plans would rewrite an
 * admin's hand-set ceilings on every member add.
 */
function PlanAuthoritySetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const authority = data.resolved.planAuthority
  const meaningful = data.resolved.plansAreMeaningful
  const manual = authority === 'manual'
  const [saving, setSaving] = useState(false)

  const storedFallback = data.settings.manualFallbackPlanId
  // Drafts over the stored values (#897) — see GraceSetting.
  const [fallback, setFallback, { clear: clearFallback, settle: settleFallback }] = useDraftState<string>('platform:planAuthority:fallback', storedFallback ?? '')
  const fallbackDirty = (fallback || null) !== storedFallback

  const storedDefault = data.settings.defaultPlanId
  const [preset, setPreset, { clear: clearPreset, settle: settlePreset }] = useDraftState<string>('platform:planAuthority:preset', storedDefault ?? '')
  const presetDirty = (preset || null) !== storedDefault

  /** `onWritten` settles the draft the write committed (#897): it stays until the
   *  reload shows the new stored value, so nothing flashes back. */
  const write = async (patch: Parameters<typeof budgetApi.setPlatformSettings>[0], msg: string, onWritten?: () => void) => {
    setSaving(true)
    try {
      await budgetApi.setPlatformSettings(patch)
      onWritten?.()
      toast.success(msg)
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  // A retired plan cannot be chosen (the route refuses it, so offering it would
  // be a form that fails on submit) — but if one is ALREADY stored, keep it in
  // the list so the picker shows what is configured rather than silently
  // appearing unset.
  const options = data.plans.filter(p => p.isActive || p.id === storedFallback)
  const chosen = data.plans.find(p => p.id === fallback) ?? null
  // Same rule for the default: a retired plan cannot be chosen (the route refuses
  // it), but one ALREADY stored stays listed so the picker shows what is
  // configured rather than appearing unset.
  const presetOptions = data.plans.filter(p => p.isActive || p.id === storedDefault)
  const chosenPreset = data.plans.find(p => p.id === preset) ?? null

  return (
    <Section
      title={t('settings.platform.settings.planAuthorityTitle', { defaultValue: "Who decides tenants' plans" })}
      desc={t('settings.platform.settings.planAuthorityDesc', {
        defaultValue:
          'Where tenants\' plans come from. Credit purchase is unaffected either way — this decides who assigns the PLAN, not whether a payment provider is connected.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3">
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex flex-col gap-1">
            <span className="themed-text-secondary text-[11px]">
              {t('settings.platform.settings.planAuthorityLabel', { defaultValue: 'Plans are decided by' })}
            </span>
            <div className="flex gap-1" role="group">
              {(['provider', 'manual'] as const).map(mode => (
                <button
                  key={mode}
                  onClick={() => {
                    if (mode === authority) return
                    void write(
                      { planAuthority: mode },
                      mode === 'manual'
                        ? t('settings.platform.settings.authorityManualSaved', {
                            defaultValue: 'Plans are now assigned here, not by the payment provider.',
                          })
                        : t('settings.platform.settings.authorityProviderSaved', {
                            defaultValue: 'Plans come from the payment provider again.',
                          }),
                    )
                  }}
                  disabled={saving || !canEdit}
                  aria-pressed={mode === authority}
                  className={`px-2.5 py-1 text-xs rounded disabled:opacity-50 ${
                    mode === authority ? 'themed-btn-primary' : 'themed-btn-secondary'
                  }`}
                >
                  {mode === 'provider'
                    ? t('settings.platform.settings.authorityProvider', { defaultValue: 'The payment provider' })
                    : t('settings.platform.settings.authorityManual', { defaultValue: 'An operator, manually' })}
                </button>
              ))}
            </div>
            <span className="themed-text-muted text-[10px]">
              {manual
                ? t('settings.platform.settings.authorityManualMeans', {
                    defaultValue: 'A platform admin assigns plans and confirms payment out of band.',
                  })
                : t('settings.platform.settings.authorityProviderMeans', {
                    defaultValue: 'Subscriptions decide. This is the built-in default.',
                  })}
            </span>
          </div>

          <StatTile
            label={t('settings.platform.settings.plansApply', { defaultValue: 'Plans set limits' })}
            value={meaningful
              ? t('settings.platform.settings.envOn', { defaultValue: 'Enabled' })
              : t('settings.platform.settings.envOff', { defaultValue: 'Disabled' })}
            hint={meaningful
              ? t('settings.platform.settings.plansApplyYes', {
                  defaultValue: 'A plan\'s caps, model access and features are being enforced.',
                })
              // The one cell worth explaining: provider + billing off. Naming the
              // flag is what stops this reading as a bug.
              : t('settings.platform.settings.plansApplyNo', {
                  defaultValue:
                    'Billing is off and plans come from the provider, so no plan changes any limit. Switch to manual, or enable billing.',
                })}
          />
        </div>

        {/* ── The default preset ─────────────────────────────────────────
            Above the fallback deliberately: a customer STARTS on the default and
            only later lands on the fallback, so the two controls read in the
            order a customer meets them. */}
        <div className="mt-4 pt-3 border-t themed-border">
          <div className="themed-text text-sm mb-0.5">
            {t('settings.platform.settings.defaultPresetTitle', { defaultValue: 'Default preset for new tenants' })}
          </div>
          <p className="themed-text-muted text-[11px] mb-2">
            {t('settings.platform.settings.defaultPresetDesc', {
              defaultValue:
                'A new tenant is created with this preset\u2019s limits copied onto them. Editing the preset afterwards does not change existing tenants \u2014 that is a separate, deliberate step.',
            })}
          </p>

          {/* Not a nag: without one, provisioning cannot copy any values and says
              so instead of quietly handing the customer a zero budget. */}
          {!storedDefault && (
            <p className="text-[11px] mb-2 text-amber-300">
              {t('settings.platform.settings.defaultPresetNone', {
                defaultValue:
                  'Nothing configured, so new tenants fall back to the free plan \u2014 usually a zero AI budget. Choose one.',
              })}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
              disabled={saving || !canEdit}
              aria-label={t('settings.platform.settings.defaultPresetTitle', { defaultValue: 'Default preset for new tenants' })}
              className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
            >
              <option value="">
                {t('settings.platform.settings.defaultPresetUnset', { defaultValue: 'Nothing configured' })}
              </option>
              {presetOptions.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {!p.isActive ? ` — ${t('settings.platform.settings.fallbackRetired', { defaultValue: 'retired' })}` : ''}
                </option>
              ))}
            </select>

            {/* The same warning the fallback carries, for the same reason — but
                here it is a mistake rather than a feature: a default preset that
                pauses uploads freezes every customer created from it. */}
            {chosenPreset?.freezesUploads && (
              <span className="text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap bg-amber-500/20 text-amber-300">
                {t('settings.platform.settings.defaultPresetFreezes', {
                  defaultValue: 'Pauses uploads — new tenants would start frozen',
                })}
              </span>
            )}
          </div>

          {canEdit && presetDirty && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => void write(
                  { defaultPlanId: preset || null },
                  t('settings.platform.settings.defaultPresetSaved', { defaultValue: 'Default preset updated.' }),
                  settlePreset,
                )}
                disabled={saving}
                className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
              >
                {saving ? t('common:saving') : t('common:save')}
              </button>
              <button
                onClick={clearPreset}
                disabled={saving}
                className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
              >
                {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
              </button>
            </div>
          )}
        </div>

        {/* ── The fallback plan ─────────────────────────────────────────── */}
        <div className="mt-4 pt-3 border-t themed-border">
          <div className="themed-text text-sm mb-0.5">
            {t('settings.platform.settings.fallbackTitle', { defaultValue: 'Non-payment fallback plan' })}
          </div>
          <p className="themed-text-muted text-[11px] mb-2">
            {t('settings.platform.settings.fallbackDesc', {
              defaultValue:
                'Where an overdue tenant is moved when an operator decides to act. Nothing applies this automatically — the Operations queue lists who is eligible and a person clicks.',
            })}
          </p>

          {!manual && (
            <p className="themed-text-muted text-[11px] mb-2">
              {t('settings.platform.settings.fallbackNotManual', {
                defaultValue:
                  'Only used when plans are assigned manually. With the provider deciding, a cancelled subscription already reverts the tenant on its own.',
              })}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <select
              value={fallback}
              onChange={(e) => setFallback(e.target.value)}
              disabled={saving || !canEdit || !manual}
              aria-label={t('settings.platform.settings.fallbackTitle', { defaultValue: 'Non-payment fallback plan' })}
              className="themed-input border rounded px-2 py-1 text-xs outline-none focus:border-blue-500 disabled:opacity-50"
            >
              <option value="">
                {t('settings.platform.settings.fallbackNone', { defaultValue: 'Nothing configured' })}
              </option>
              {options.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {!p.isActive ? ` — ${t('settings.platform.settings.fallbackRetired', { defaultValue: 'retired' })}` : ''}
                </option>
              ))}
            </select>

            {/* The consequence of the CHOICE, not of what is saved — an operator
                needs to see this before they commit, and "does this pause their
                uploads" is the only thing about the fallback that matters. */}
            {chosen && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap ${
                chosen.freezesUploads ? 'bg-amber-500/20 text-amber-300' : 'themed-bg-tertiary themed-text-muted'
              }`}>
                {chosen.freezesUploads
                  ? t('settings.platform.settings.fallbackFreezes', { defaultValue: 'Pauses uploads' })
                  : t('settings.platform.settings.fallbackNoFreeze', { defaultValue: 'Uploads keep working' })}
              </span>
            )}
          </div>

          {canEdit && manual && fallbackDirty && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => void write(
                  { manualFallbackPlanId: fallback || null },
                  t('settings.platform.settings.fallbackSaved', { defaultValue: 'Fallback plan updated.' }),
                  settleFallback,
                )}
                disabled={saving}
                className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
              >
                {saving ? t('common:saving') : t('common:save')}
              </button>
              <button
                onClick={clearFallback}
                disabled={saving}
                className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
              >
                {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
              </button>
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}

/**
 * Which cost the AI assistant's chat window shows, deployment-wide (0174).
 *
 * ── THREE options, not four: there is no "Not set" ──────────────────────
 *
 * The column is still nullable and `null` is still accepted over the wire, but
 * this editor neither offers nor mints it. Unset RESOLVES to 'hidden'
 * (`defaultChatCostVisibility`), so a fourth button would be a second spelling
 * of the third — and worse than merely redundant, because until 2026-08-18 the
 * two were not equivalent and looked as though they were. Unset used to mean
 * operator cost in the platform org and customer cost everywhere else; from
 * inside the platform org, where an operator actually tests this, operator cost
 * renders no per-message badge and no live mid-turn figure, so on a quiet
 * workspace "Not set" was pixel-identical to "Hidden" while doing the opposite
 * thing to every customer on the deployment.
 *
 * A stored `null` therefore renders with **Hidden** pressed — same state, one
 * name. Legacy rows and API callers keep working; nothing here creates a new
 * one.
 *
 * ── Why there is still no "resolved" figure beside the control ──────────
 *
 * Every other setting on this page renders its stored value next to what is
 * actually in force, because the chain ends in ONE answer the whole deployment
 * shares. This one still does not, and removing the per-org branch did not fix
 * that: the result is CLAMPED per viewer, so 'operator' is what a platform admin
 * sees and 'user' is what everyone else sees from the identical stored value.
 * A single "in force" line would be a confident wrong number for most people who
 * open a chat — the exact failure `graceCapSource` exists to prevent, reproduced
 * by trying to look like it. Each option names its own effect instead.
 *
 * ── Why the operator option is not dangerous to offer ───────────────────
 *
 * Choosing "what the platform pays" does not disclose anything to customers.
 * `resolveChatCostVisibility` downgrades it to customer cost for any viewer who
 * is not a platform admin, and the server never computes the provider figure for
 * such a caller — so this control can only change what a PLATFORM ADMIN sees. The
 * copy says so, because an operator who does not know that will not use it.
 *
 * Saves on click like `PlanAuthoritySetting`, not behind a Save bar like the
 * ceilings. The staged-edit rule exists for money VALUES, where a mistyped digit
 * and a stray click move a real limit; this is a three-way mode with its effect
 * named on every button and nothing to mistype.
 */
function ChatCostSetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const [saving, setSaving] = useState(false)
  // A stored `null` IS 'hidden' — see the header. Selection, the no-op guard and
  // the explanation below all read this, never `stored`, so the resting state of
  // an unconfigured deployment shows Hidden pressed rather than nothing pressed.
  const effective: ChatCostVisibility = data.settings.chatCostVisibility ?? 'hidden'

  const OPTIONS: Array<{ value: ChatCostVisibility; label: string; means: string }> = [
    {
      value: 'user',
      label: t('settings.platform.settings.chatCostUser', { defaultValue: 'What the customer is charged' }),
      means: t('settings.platform.settings.chatCostUserMeans', {
        defaultValue: 'The figure their spending ceilings are measured against.',
      }),
    },
    {
      value: 'operator',
      label: t('settings.platform.settings.chatCostOperator', { defaultValue: 'What the platform pays' }),
      means: t('settings.platform.settings.chatCostOperatorMeans', {
        defaultValue: 'Your own provider cost. Only platform admins ever see it — everyone else falls back to what they are charged.',
      }),
    },
    {
      value: 'hidden',
      label: t('settings.platform.settings.chatCostHidden', { defaultValue: 'Hidden' }),
      means: t('settings.platform.settings.chatCostHiddenMeans', {
        defaultValue: 'No cost anywhere in the chat window. This is what an unconfigured deployment does. Spending screens are unaffected.',
      }),
    },
  ]
  const current = OPTIONS.find(o => o.value === effective) ?? OPTIONS[2]!

  const choose = async (value: ChatCostVisibility) => {
    // Compared against the RESOLVED state, not the stored one: on a deployment
    // that has never been configured, clicking the already-pressed Hidden must
    // be a no-op rather than a write that only changes `null` to `'hidden'`.
    if (value === effective) return
    setSaving(true)
    try {
      await budgetApi.setPlatformSettings({ chatCostVisibility: value })
      toast.success(t('settings.platform.settings.chatCostSaved', {
        defaultValue: 'Chat cost display updated. Users see it on their next load.',
      }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section
      title={t('settings.platform.settings.chatCostTitle', { defaultValue: 'Cost shown in the AI assistant' })}
      desc={t('settings.platform.settings.chatCostDesc', {
        defaultValue:
          'The per-message, session and workspace figures inside the chat window. This does not change any spending screen, any ceiling, or what a customer is billed. Individual tenants can be set differently on their Configuration tab.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3">
        <div className="flex flex-col gap-1">
          <span className="themed-text-secondary text-[11px]">
            {t('settings.platform.settings.chatCostLabel', { defaultValue: 'Chat shows' })}
          </span>
          <div className="flex flex-wrap gap-1" role="group">
            {OPTIONS.map(o => (
              <button
                key={o.value}
                onClick={() => { void choose(o.value) }}
                disabled={saving || !canEdit}
                aria-pressed={o.value === effective}
                className={`px-2.5 py-1 text-xs rounded disabled:opacity-50 ${
                  o.value === effective ? 'themed-btn-primary' : 'themed-btn-secondary'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <span className="themed-text-muted text-[10px]">{current.means}</span>
        </div>
      </div>
    </Section>
  )
}

/**
 * The platform-wide keybook switch (0179).
 *
 * ── What "off" actually does, so the copy can say it honestly ────────────
 *
 * Three enforcement points, all reading `resolveKeybookEnabled` on the same
 * column this saves: the `/api/keybook/*` gate (every keybook request refused
 * in words), the `cred_to_env` agent tool (the assistant is told credential
 * storage is unavailable rather than prompting), and the SPA (the taskbar
 * item, the keybook page, the mobile tab and Settings → Secure Storage all
 * hide). Nothing STORED is touched — keybook entries, per-tenant enablement
 * flags and session credential bindings all survive, so switching back
 * restores everything as it was. That reversibility is why the confirm below
 * is not a type-to-confirm ceremony.
 *
 * ── Two rows, not three ─────────────────────────────────────────────────
 *
 * The stored column is tri-state (`null` = not chosen → available), but unlike
 * the personal-tenant mode there is no third OUTCOME to represent: an explicit
 * `true` and an inherited `true` behave identically everywhere, and no brand
 * or env layer will ever sit between them. So "Available" saves `null` (clear
 * the choice) and "Disabled" saves `false`, and the audit trail reads
 * "not set → off" — which is the honest history.
 */
function KeybookSetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()

  const storedOff = data.settings.keybookEnabled === false
  // A draft over what PERSISTED (#897, kept across a phone/desktop layout
  // swap) — same rule as the personal-tenant mode control above.
  const [off, setOff, { clear: clearOff, settle: settleOff }] = useDraftState<boolean>('platform:keybook', storedOff)
  const [saving, setSaving] = useState(false)

  const dirty = off !== storedOff

  const save = async () => {
    if (off && !storedOff) {
      const ok = await confirm({
        title: t('settings.platform.settings.keybookConfirmTitle', {
          defaultValue: 'Switch the keybook off?',
        }),
        message: t('settings.platform.settings.keybookConfirm', {
          defaultValue:
            'The keybook page disappears for everyone, the keybook API refuses every request, and the AI assistant can no longer request NEW credentials (cred_to_env). Credentials already saved in the keybook remain usable in chat sessions. Nothing stored is deleted — every entry and setting comes back exactly as it was if you switch this back on.',
        }),
        confirmText: t('settings.platform.settings.keybookConfirmOk', {
          defaultValue: 'Switch keybook off',
        }),
        type: 'danger',
      })
      if (!ok) return
    }
    setSaving(true)
    try {
      // `null`, not `true`, for the on state: an explicit true and the shipped
      // default behave identically, so the honest record is "no choice stored".
      await budgetApi.setPlatformSettings({ keybookEnabled: off ? false : null })
      settleOff()
      toast.success(t('settings.platform.settings.keybookSaved', { defaultValue: 'Keybook setting updated.' }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const rows = [
    {
      off: false,
      label: t('settings.platform.settings.keybookOn', { defaultValue: 'Available (shipped default)' }),
      blurb: t('settings.platform.settings.keybookOnBlurb', {
        defaultValue:
          'Users manage credentials on the keybook page, and the AI assistant can request them securely via cred_to_env.',
      }),
    },
    {
      off: true,
      label: t('settings.platform.settings.keybookOff', { defaultValue: 'Disabled' }),
      blurb: t('settings.platform.settings.keybookOffBlurb', {
        defaultValue:
          'The page is hidden, the keybook API refuses every request, and the assistant cannot request new credentials. Credentials already saved in the keybook remain usable in chat sessions; stored entries are kept and come back if this is switched on again.',
      }),
    },
  ]

  return (
    <Section
      title={t('settings.platform.settings.keybookTitle', { defaultValue: 'Keybook' })}
      desc={t('settings.platform.settings.keybookDesc', {
        defaultValue:
          'Whether credential storage exists on this deployment at all — the keybook page, its API, and the assistant’s cred_to_env tool.',
      })}
    >
      <div
        role="radiogroup"
        aria-label={t('settings.platform.settings.keybookTitle', { defaultValue: 'Keybook' })}
        className="themed-bg-secondary rounded-lg p-3 flex flex-col gap-2"
      >
        {rows.map((row) => {
          const active = off === row.off
          return (
            <button
              key={String(row.off)}
              role="radio"
              aria-checked={active}
              onClick={() => setOff(row.off)}
              disabled={saving || !canEdit}
              data-testid={`keybook-option-${row.off ? 'off' : 'on'}`}
              className={`flex items-start gap-3 text-left rounded-lg px-3 py-2 transition-colors disabled:cursor-default ${
                active ? 'ring-1 ring-[color:var(--bill-primary)] bg-[color:var(--bill-tint)]' : canEdit ? 'hover:themed-bg-tertiary' : 'opacity-70'
              }`}
            >
              <span
                className={`mt-0.5 h-4 w-4 shrink-0 rounded-full ring-1 flex items-center justify-center ${
                  active ? 'ring-[color:var(--bill-primary)]' : 'ring-[color:var(--border-secondary)]'
                }`}
                aria-hidden
              >
                {active && <span className="h-2 w-2 rounded-full bg-[color:var(--bill-primary)]" />}
              </span>
              <span className="min-w-0">
                <span className="block text-sm themed-text">{row.label}</span>
                <span className="block text-xs themed-text-muted mt-0.5">{row.blurb}</span>
              </span>
            </button>
          )
        })}

        <div className="flex flex-wrap items-center justify-between gap-3 mt-1">
          <p className="themed-text-muted text-[11px] max-w-xl">
            {t('settings.platform.settings.keybookImmediate', {
              defaultValue:
                'The API and the assistant follow within about half a minute; open browser tabs hide the page on their next load.',
            })}
          </p>
          {canEdit && dirty && (
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={clearOff}
                disabled={saving}
                className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
              >
                {t('settings.budget.pricing.discard', { defaultValue: 'Discard' })}
              </button>
              <button
                onClick={save}
                data-testid="keybook-save"
                disabled={saving}
                className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
              >
                {saving ? t('common:saving') : t('common:save')}
              </button>
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}

/**
 * The disk-alarm webhook (0200) — the alarm's SECOND delivery channel, beside
 * email to PLATFORM_ADMIN_EMAILS.
 *
 * It is a SECRET, and that shapes the whole control: the URL is a bearer
 * capability, so the server never sends it back — the page knows only whether
 * one is CONFIGURED (`data.settings.diskAlarmWebhookConfigured`), never the
 * value. So there is nothing to pre-fill and nothing to reveal: the input
 * always starts empty, "Save" ARMS or REPLACES with whatever was typed, and a
 * separate "Clear" removes it. A replace is blind by design — you cannot see
 * the URL you are overwriting, only that one exists.
 */
function DiskAlarmWebhookSetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()

  // `=== true`, never a bare read: the field is OPTIONAL (a pre-0200 API instance
  // omits it during the deploy that introduces this feature). `undefined` →
  // treated as not-configured, which is SAFE here in a way it would not be for
  // an existing setting: the `disk_alarm_webhook` column does not exist until
  // 0200 is applied, so no webhook can be armed before this feature is fully
  // deployed, and the state self-heals on the next load. Same rolling-restart
  // discipline `LandingEditor` documents, minus a fallback value it does not need.
  const configured = data.settings.diskAlarmWebhookConfigured === true
  const [url, setUrl] = useState('')
  const [saving, setSaving] = useState(false)

  const trimmed = url.trim()
  // Light client-side shape check for immediate feedback only — the route's
  // `validateWebhookUrl` is the authority and rejects anything this misses.
  const looksValid = /^https?:\/\/\S+$/i.test(trimmed)
  const canArm = trimmed.length > 0 && looksValid

  const arm = async () => {
    if (!canArm) return
    setSaving(true)
    try {
      await budgetApi.setPlatformSettings({ diskAlarmWebhook: trimmed })
      toast.success(t('settings.platform.settings.diskWebhookSaved', {
        defaultValue: 'Disk alarm webhook armed.',
      }))
      setUrl('')
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const clear = async () => {
    const ok = await confirm({
      title: t('settings.platform.settings.diskWebhookClearTitle', {
        defaultValue: 'Remove the disk alarm webhook?',
      }),
      message: t('settings.platform.settings.diskWebhookClearBody', {
        defaultValue:
          'The disk alarm falls back to email only (PLATFORM_ADMIN_EMAILS). Nothing else changes — the alarm still fires; it just loses this second channel.',
      }),
      confirmText: t('settings.platform.settings.diskWebhookClearOk', {
        defaultValue: 'Remove webhook',
      }),
      type: 'danger',
    })
    if (!ok) return
    setSaving(true)
    try {
      await budgetApi.setPlatformSettings({ diskAlarmWebhook: null })
      toast.success(t('settings.platform.settings.diskWebhookCleared', {
        defaultValue: 'Disk alarm webhook removed.',
      }))
      setUrl('')
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section
      title={t('settings.platform.settings.diskWebhookTitle', { defaultValue: 'Disk alarm webhook' })}
      desc={t('settings.platform.settings.diskWebhookDesc', {
        defaultValue:
          'A Slack-shaped incoming webhook the disk-space alarm posts to, beside email. Optional — a second way for a low-disk warning to reach a human before writes start failing.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3 flex flex-col gap-3">
        {/* Status — presence only. The URL is never shown. */}
        <div className="flex items-center gap-2" data-testid="disk-webhook-status">
          <span
            className={`h-2 w-2 rounded-full ${configured ? 'bg-emerald-500' : 'bg-[color:var(--border-secondary)]'}`}
            aria-hidden
          />
          <span className="text-sm themed-text">
            {configured
              ? t('settings.platform.settings.diskWebhookConfigured', { defaultValue: 'Configured' })
              : t('settings.platform.settings.diskWebhookNotConfigured', { defaultValue: 'Not configured — email only' })}
          </span>
        </div>

        {canEdit && (
          <label className="flex flex-col gap-1">
            <span className="text-xs themed-text-muted">
              {configured
                ? t('settings.platform.settings.diskWebhookReplaceLabel', { defaultValue: 'Replace with a new URL' })
                : t('settings.platform.settings.diskWebhookSetLabel', { defaultValue: 'Webhook URL' })}
            </span>
            <input
              type="url"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={saving}
              data-testid="disk-webhook-input"
              placeholder="https://hooks.slack.com/services/…"
              aria-label={t('settings.platform.settings.diskWebhookSetLabel', { defaultValue: 'Webhook URL' })}
              className="themed-input text-sm px-2 py-1.5 rounded font-mono"
            />
            <span className="text-[11px] themed-text-muted min-h-[1rem]">
              {trimmed.length > 0 && !looksValid
                ? t('settings.platform.settings.diskWebhookInvalid', {
                    defaultValue: 'Must be an http(s) URL.',
                  })
                : configured
                  ? t('settings.platform.settings.diskWebhookReplaceHint', {
                      defaultValue: 'The current URL is hidden. Saving overwrites it.',
                    })
                  : t('settings.platform.settings.diskWebhookSetHint', {
                      defaultValue: 'Stored write-only — it is never shown again after you save.',
                    })}
            </span>
          </label>
        )}
        {!canEdit && <ReadOnlyNotice />}

        {canEdit && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            {configured && (
              <button
                onClick={clear}
                disabled={saving}
                data-testid="disk-webhook-clear"
                className="px-2.5 py-1 text-xs rounded themed-btn-secondary disabled:opacity-50"
              >
                {t('settings.platform.settings.diskWebhookClear', { defaultValue: 'Remove' })}
              </button>
            )}
            <button
              onClick={arm}
              disabled={saving || !canArm}
              data-testid="disk-webhook-save"
              className="px-3 py-1.5 text-xs rounded themed-btn-primary disabled:opacity-50"
            >
              {saving
                ? t('common:saving')
                : configured
                  ? t('settings.platform.settings.diskWebhookReplace', { defaultValue: 'Replace' })
                  : t('settings.platform.settings.diskWebhookArm', { defaultValue: 'Arm webhook' })}
            </button>
          </div>
        )}
      </div>
    </Section>
  )
}

/**
 * The disk-alarm's EMAIL recipients (0203, #574), the alarm's FIRST delivery
 * channel, beside the webhook above.
 *
 * ── Why this control looks nothing like its neighbour ──────────────────────
 *
 * `DiskAlarmWebhookSetting` is shaped entirely by the URL being a SECRET: the
 * server never returns it, so the input starts empty, "Save" arms-or-replaces
 * blind, and status is a dot rather than a value.
 *
 * None of that applies here. These are the operator's own addresses; being on
 * the list grants nothing. So the control is an ordinary editable field,
 * PRE-FILLED with the stored value, an operator must be able to see and amend
 * the list they are accountable for, not overwrite it blind.
 *
 * ── Empty means INHERIT, not "nobody" ──────────────────────────────────────
 *
 * Clearing the box restores the `PLATFORM_ADMIN_EMAILS` fallback rather than
 * disabling email. That is why the placeholder and the empty state name the
 * fallback instead of saying "none": an operator who empties this field has not
 * turned email off, and a screen implying otherwise would be actively
 * misleading during an incident. The route enforces the same reading, a string
 * parsing to no addresses is stored as `null`.
 */
function DiskAlarmEmailsSetting({
  data, canEdit, onSaved,
}: {
  data: PlatformSettingsResponse
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()

  // `?? ''`, the field is OPTIONAL, because an API instance predating 0203
  // omits it during the rolling restart that ships this. `undefined` and `null`
  // both mean "not configured", which renders as the fallback notice below.
  const stored = data.settings.diskAlarmEmails ?? ''
  // A draft over the stored value (#897, kept across a phone/desktop layout
  // swap). A successful save drops it, so the box then shows what the server
  // normalised (`a@x.com,b@x.com` comes back as `a@x.com, b@x.com`) rather than
  // the raw text typed; until typed in, a reload shows through.
  const [value, setValue, { settle: settleValue }] = useDraftState<string>('platform:diskAlarmEmails', stored)
  const [saving, setSaving] = useState(false)

  const trimmed = value.trim()
  const dirty = trimmed !== stored
  // Immediate feedback only, the route is the authority and rejects anything
  // this misses. Deliberately permissive for the same reason it is on the
  // server: refusing a deliverable address is worse than accepting a typo the
  // operator can see sitting in the box.
  // The SAME rules the route validates with and the alarm resolves with, they
  // live in `@papan/types` precisely so this browser file can reach them. A
  // local copy here is how a screen comes to accept a value the server rejects,
  // or warn about one it would have taken.
  const parsed = parseDiskAlarmEmails(trimmed)
  const invalid = invalidDiskAlarmEmails(trimmed)
  // The SAME bound the route enforces. Immediate feedback only — the route is
  // still the authority — but an operator who pasted an address book should see
  // it here rather than get a 400 after hitting Save.
  const tooMany = parsed.length > MAX_DISK_ALARM_RECIPIENTS
  const canSave = dirty && invalid.length === 0 && !tooMany

  async function save() {
    setSaving(true)
    try {
      // Empty box → `null`, which restores the fallback. Sending `''` would be
      // rejected by the column's CHECK, and sending an empty list would be a
      // request to email nobody, neither is what an operator emptying a field
      // means.
      const sent = parsed.length > 0 ? parsed.join(', ') : null
      await budgetApi.setPlatformSettings({ diskAlarmEmails: sent })
      // As STORED (normalised): a save that left the stored list unchanged
      // (only spacing differed) must not leave the raw text looking unsaved.
      settleValue(sent ?? '')
      toast.success(parsed.length > 0
        ? t('settings.platform.settings.diskEmailsSaved', { defaultValue: 'Disk alarm recipients updated.' })
        : t('settings.platform.settings.diskEmailsCleared', {
            defaultValue: 'Disk alarm recipients cleared. Falling back to PLATFORM_ADMIN_EMAILS.',
          }))
      onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section
      title={t('settings.platform.settings.diskEmailsTitle', { defaultValue: 'Disk alarm recipients' })}
      desc={t('settings.platform.settings.diskEmailsDesc', {
        defaultValue:
          'Who receives the disk-space alarm by email. Leave this empty to use the platform admins (PLATFORM_ADMIN_EMAILS), which is what happens today.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3 flex flex-col gap-3">
        <div className="flex items-center gap-2" data-testid="disk-emails-status">
          <span
            className={`h-2 w-2 rounded-full ${stored ? 'bg-emerald-500' : 'bg-[color:var(--border-secondary)]'}`}
            aria-hidden
          />
          <span className="text-sm themed-text">
            {stored
              ? t('settings.platform.settings.diskEmailsConfigured', { defaultValue: 'Configured' })
              : t('settings.platform.settings.diskEmailsInherited', {
                  defaultValue: 'Not set. Using the platform admins (PLATFORM_ADMIN_EMAILS)',
                })}
          </span>
        </div>

        {canEdit ? (
          <label className="flex flex-col gap-1">
            <span className="text-xs themed-text-muted">
              {t('settings.platform.settings.diskEmailsLabel', { defaultValue: 'Email addresses, comma-separated' })}
            </span>
            <input
              type="text"
              inputMode="email"
              autoComplete="off"
              spellCheck={false}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              disabled={saving}
              data-testid="disk-emails-input"
              placeholder="ops@example.com, oncall@example.com"
              aria-label={t('settings.platform.settings.diskEmailsLabel', { defaultValue: 'Email addresses, comma-separated' })}
              className="themed-input text-sm px-2 py-1.5 rounded font-mono"
            />
            {tooMany && (
              <p className="text-xs text-amber-500">
                {t('settings.platform.settings.diskEmailsTooMany', {
                  count: parsed.length,
                  max: MAX_DISK_ALARM_RECIPIENTS,
                  defaultValue:
                    '{{count}} addresses — at most {{max}} are allowed. The alarm sends one email per '
                    + 'recipient, so this list is a fan-out factor during an incident.',
                })}
              </p>
            )}
            {invalid.length > 0 && (
              <span className="text-xs text-red-400" data-testid="disk-emails-invalid">
                {t('settings.platform.settings.diskEmailsInvalid', {
                  defaultValue: 'Not a valid address: {{list}}',
                  list: invalid.join(', '),
                })}
              </span>
            )}
            <span className="text-xs themed-text-muted">
              {t('settings.platform.settings.diskEmailsHint', {
                defaultValue: 'Clearing this box does not switch email off. It falls back to the platform admins.',
              })}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => void save()}
                disabled={!canSave || saving}
                data-testid="disk-emails-save"
                className="themed-btn-primary text-sm px-3 py-1.5 rounded disabled:opacity-50"
              >
                {saving
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : t('common.save', { defaultValue: 'Save' })}
              </button>
            </div>
          </label>
        ) : (
          // A read-only seat still sees the list, it is not a secret, and hiding
          // it would leave a viewer unable to answer "who gets paged?".
          stored && <div className="text-sm themed-text font-mono">{stored}</div>
        )}
      </div>
    </Section>
  )
}
